import type { ElementType } from 'react';
import {
  AlertOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import { Button, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { NHAN_TRANG_THAI, TONG_TRANG_THAI } from '../../constants';
import { phanTram, SAP_DEN_HAN_NGAY } from '../../nghiep-vu/nhiem-vu';
import type { LocTrangThai, ThongKeNhiemVu } from '../../types-nhiem-vu';

interface Props {
  dashboard: ThongKeNhiemVu | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  active: LocTrangThai | '';
  onPick: (s: LocTrangThai | '') => void;
}

interface Kpi {
  filter: LocTrangThai | '';
  value: (d: ThongKeNhiemVu) => number;
  icon: ElementType;
}

const KPIS: Kpi[] = [
  { filter: '', value: (d) => d.total, icon: UnorderedListOutlined },
  { filter: 'MOI_PHAN_CONG', value: (d) => d.moi_phan_cong, icon: PauseCircleOutlined },
  { filter: 'DANG_THUC_HIEN', value: (d) => d.dang_thuc_hien, icon: PlayCircleOutlined },
  { filter: 'SAP_DEN_HAN', value: (d) => d.sap_den_han, icon: FieldTimeOutlined },
  { filter: 'QUA_HAN', value: (d) => d.qua_han, icon: AlertOutlined },
  { filter: 'HOAN_THANH', value: (d) => d.hoan_thanh, icon: CheckCircleOutlined },
];

/** Sáu thẻ đếm; bấm thẻ để lọc danh sách theo trạng thái đó, bấm lại để bỏ lọc. */
export function TaskKpiStrip({ dashboard: d, loading, error, onRetry, active, onPick }: Props) {
  const { t, locale } = useT();

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        <p className="text-sm text-danger-700">{t('vanBanNhiemVu.kpi.loi')}</p>
        <Button variant="outline" size="sm" icon={<ReloadOutlined />} onClick={onRetry}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      {KPIS.map((k) => {
        const dangChon = k.filter === active;
        const value = d ? k.value(d) : 0;
        const tone = k.filter ? TONG_TRANG_THAI[k.filter] : null;
        const Icon = k.icon;
        const nhan = k.filter ? t(NHAN_TRANG_THAI[k.filter]) : t('vanBanNhiemVu.kpi.tong');
        const goiY = !d
          ? ''
          : k.filter === ''
            ? t('vanBanNhiemVu.kpi.tongMo')
            : k.filter === 'SAP_DEN_HAN'
              ? t('vanBanNhiemVu.kpi.sapDenHanMo', { so: SAP_DEN_HAN_NGAY })
              : t('vanBanNhiemVu.kpi.tyLe', { phanTram: phanTram(value, d.total) });
        const baoDong = (k.filter === 'QUA_HAN' || k.filter === 'SAP_DEN_HAN') && value > 0;
        return (
          <li key={k.filter || 'total'}>
            <button
              type="button"
              aria-pressed={dangChon}
              title={t('vanBanNhiemVu.kpi.locMo')}
              onClick={() => onPick(dangChon ? '' : k.filter)}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg border bg-surface px-4 py-3 text-left shadow-sm transition hover:border-primary-300',
                dangChon ? 'border-primary-500 ring-2 ring-primary-100' : 'border-default',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                  tone ? tone.chip : 'bg-primary-50 text-primary-600',
                )}
              >
                <Icon aria-hidden />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium text-content-secondary">{nhan}</span>
                {loading && !d ? (
                  <Skeleton className="mt-1 h-6 w-10" />
                ) : (
                  <span
                    className={cn(
                      'block text-xl font-bold tabular-nums',
                      baoDong && tone ? tone.value : 'text-content-primary',
                    )}
                  >
                    {soVN(value, locale)}
                  </span>
                )}
                <span className="block truncate text-2xs text-content-muted">{goiY}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
